import React, { useEffect, useState } from 'react';
import { Plus, Ban, Copy, Check } from 'lucide-react';
import api from '../../utils/api';

const fmtDate = (s) => s ? new Date(s).toLocaleDateString('pt-BR') : '—';

export default function AdminTokens() {
  const [tokens, setTokens] = useState([]);
  const [escopos, setEscopos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [newToken, setNewToken] = useState(null);
  const [form, setForm] = useState({ nome: '', escopos: [], expira_em: '' });
  const [copied, setCopied] = useState(false);

  const load = () => {
    setLoading(true);
    Promise.all([api.get('/admin/tokens'), api.get('/admin/tokens/escopos')])
      .then(([t, e]) => { setTokens(t.data); setEscopos(e.data); }).finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  const create = async (e) => {
    e.preventDefault();
    const r = await api.post('/admin/tokens', form);
    setNewToken(r.data);
    setShowModal(false);
    setForm({ nome: '', escopos: [], expira_em: '' });
    load();
  };

  const revoke = async (t) => {
    await api.post(`/admin/tokens/${t.id}/revogar`);
    load();
  };

  const copy = () => {
    navigator.clipboard.writeText(newToken.valor);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const toggleScope = (s) => setForm(f => ({
    ...f, escopos: f.escopos.includes(s) ? f.escopos.filter(x => x !== s) : [...f.escopos, s]
  }));

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-xl font-bold text-stone-900">Tokens / API</h1>
        <button onClick={() => setShowModal(true)} className="flex items-center gap-2 bg-[#0f3d2e] text-white text-sm font-medium px-4 py-2 rounded-lg hover:bg-[#0a2e22]">
          <Plus size={16} /> Novo Token
        </button>
      </div>

      <div className="bg-white border border-stone-200 rounded-xl overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-stone-50 text-stone-500 text-xs">
            <tr><th className="text-left px-4 py-3">Nome</th><th className="text-left px-4 py-3">Sufixo</th><th className="text-left px-4 py-3">Escopos</th><th className="text-left px-4 py-3">Status</th><th className="text-left px-4 py-3">Criado</th><th className="text-right px-4 py-3">Ações</th></tr>
          </thead>
          <tbody className="divide-y divide-stone-50">
            {loading ? <tr><td colSpan={6} className="px-4 py-8 text-center text-stone-400">Carregando...</td></tr>
              : tokens.length === 0 ? <tr><td colSpan={6} className="px-4 py-8 text-center text-stone-400">Nenhum token</td></tr>
              : tokens.map(t => (
                <tr key={t.id} className="hover:bg-stone-50">
                  <td className="px-4 py-3 font-medium text-stone-800">{t.nome}</td>
                  <td className="px-4 py-3 font-mono text-stone-500">···{t.sufixo}</td>
                  <td className="px-4 py-3"><div className="flex flex-wrap gap-1">{(t.escopos || []).map(s => <span key={s} className="text-[9px] font-bold bg-indigo-50 text-indigo-600 px-1.5 py-0.5 rounded">{s}</span>)}</div></td>
                  <td className="px-4 py-3"><span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${t.status === 'ativo' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-600'}`}>{t.status}</span></td>
                  <td className="px-4 py-3 text-stone-400">{fmtDate(t.created_at)}</td>
                  <td className="px-4 py-3 text-right">
                    {t.status === 'ativo' && <button onClick={() => revoke(t)} title="Revogar" className="p-1.5 rounded-md hover:bg-red-50 text-stone-400 hover:text-red-500"><Ban size={14} /></button>}
                  </td>
                </tr>
              ))}
          </tbody>
        </table>
      </div>

      {newToken && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl w-full max-w-md shadow-xl p-6">
            <p className="font-semibold text-stone-800 mb-1">Token criado com sucesso</p>
            <p className="text-sm text-stone-500 mb-4">Copie agora — este valor não será exibido novamente.</p>
            <div className="flex items-center gap-2 bg-stone-50 border border-stone-200 rounded-lg px-3 py-2 mb-4">
              <code className="flex-1 text-xs text-stone-700 break-all">{newToken.valor}</code>
              <button onClick={copy} className="shrink-0 text-stone-500 hover:text-stone-800">{copied ? <Check size={16} className="text-green-500" /> : <Copy size={16} />}</button>
            </div>
            <button onClick={() => setNewToken(null)} className="w-full bg-[#0f3d2e] text-white rounded-lg py-2.5 text-sm font-medium">Fechar</button>
          </div>
        </div>
      )}

      {showModal && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl w-full max-w-md shadow-xl">
            <div className="px-6 py-4 border-b border-stone-100 font-semibold text-stone-800">Novo Token</div>
            <form onSubmit={create} className="p-6 space-y-4">
              <div><label className="text-xs font-medium text-stone-600 block mb-1">Nome descritivo *</label>
                <input required value={form.nome} onChange={e => setForm(f => ({...f, nome: e.target.value}))} className="w-full border border-stone-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#0f3d2e]" /></div>
              <div><label className="text-xs font-medium text-stone-600 block mb-2">Escopos *</label>
                <div className="space-y-2">
                  {escopos.map(s => (
                    <label key={s} className="flex items-center gap-2 cursor-pointer">
                      <input type="checkbox" checked={form.escopos.includes(s)} onChange={() => toggleScope(s)} className="rounded" />
                      <span className="text-sm text-stone-700 font-mono">{s}</span>
                    </label>
                  ))}
                </div></div>
              <div><label className="text-xs font-medium text-stone-600 block mb-1">Expira em</label>
                <input type="date" value={form.expira_em} onChange={e => setForm(f => ({...f, expira_em: e.target.value}))} className="w-full border border-stone-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#0f3d2e]" /></div>
              <div className="flex gap-3 pt-2">
                <button type="button" onClick={() => setShowModal(false)} className="flex-1 border border-stone-200 rounded-lg py-2.5 text-sm font-medium text-stone-600">Cancelar</button>
                <button type="submit" disabled={form.escopos.length === 0} className="flex-1 bg-[#0f3d2e] text-white rounded-lg py-2.5 text-sm font-medium hover:bg-[#0a2e22] disabled:opacity-50">Criar</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
